import Uppy from '@uppy/core';
import Tus from '@uppy/tus';
import EnhancedCompressor from '../EnhancedCompressor';
import EnhancedImageEditor from '../EnhancedImageEditor';
import { RemoteImageDownloader, LoadingStatus } from '../../plugins';
import prepareFileName from '../../utils/prepareFileName';

import TusdConfig from './TusdConfig';
import CompressorConfig from './CompressorConfig';
import ImageEditorConfig from './ImageEditorConfig';
import ImageEditorHandler from './ImageEditorHandler';
import FileTypesConfig from './FileTypesConfig';
import UploadCompleteHandler from './UploadCompleteHandler';
import EventPresenter from '../presenters/EventPresenter';
import FilePresenter from '../presenters/FilePresenter';

const defaultFn = () => {};

const FILENAME_LENGTH_LIMIT = 250;

const renameFiles = (files) => {
  const updatedFiles = {};
  Object.keys(files).forEach((fileID) => {
    updatedFiles[fileID] = files[fileID];
    const { extension, displayName, uniqueName } = prepareFileName(updatedFiles[fileID]);
    updatedFiles[fileID].extension = extension;
    updatedFiles[fileID].displayName = displayName;
    updatedFiles[fileID].name = uniqueName;
    updatedFiles[fileID].meta.name = uniqueName;
    if (updatedFiles[fileID].data) {
      // In order for Compressor to work, a 'data' should have a 'name' attribute
      try {
        updatedFiles[fileID].data.name = uniqueName;
      } catch (err) {
        // no-op. This can happen when there is no 'name' setter on the data object. Ex: File object
      }
    }
    updatedFiles[fileID].meta.source = updatedFiles[fileID].meta.source || updatedFiles[fileID].source;
  });
  return updatedFiles;
};

const validateFileNames = (uppy, files) => {
  let valid = true;
  Object.keys(files).forEach((fileID) => {
    const currentName = files[fileID].meta.name;
    if (!currentName || currentName.length < 1) {
      uppy.info('Filename is required.', 'error');
      valid = false;
    }
    if (currentName.length > FILENAME_LENGTH_LIMIT) {
      uppy.info(`Filename cannot exceed ${FILENAME_LENGTH_LIMIT} characters.`, 'error');
      valid = false;
    }
  });
  return valid;
};

const convertOptionsPresent = (opts) => (
  opts.convertOptions
  && Object.keys(opts.convertOptions).length > 0
);

const preprocessFile = (uppy, file) => {
  // don't allow arbitrary uploads
  if (!uppy.opts.restrictions.allowedFileTypes) {
    uppy.log("Aborting upload because allowed file types aren't set", 'warning');
    uppy.info('This file is not permitted', 'error');
    return false;
  }

  if (file.isRemote && file.type.startsWith('image/') && (!uppy.opts.autoProceed || convertOptionsPresent(uppy.opts))) {
    // Allow editing/compressing of a remote image:
    //  when auto proceed is disabled (allow editor)
    //  or when convert options aren't present (compressor)
    uppy.getPlugin('RemoteImageDownloader').addFile(file);
    return false;
  }
  return file;
};

// callbackHandlers
// Returns an object callback functions.
// It constructs the callbacks with scoped data passed as the second argument. It interacts
// with the `cachedFilePresenters` array argument by pushing new FilePresenters during
// `onBeforeUpload` and updating cached FilePresenters during subsequent callbacks.
const callbackHandlers = (
  {
    onBeforeUpload = defaultFn,
    onComplete = defaultFn,
    onSuccess = defaultFn,
    onError = defaultFn,
    onFileRemoved = defaultFn,
  },
  {
    cachedFilePresenters,
    logger,
  }
) => ({
  onBeforeUpload: (filePresenters) => {
    cachedFilePresenters.push(...filePresenters);
    const event = new EventPresenter({ filePresenters });
    logger.info('onBeforeUpload', { ...event });
    onBeforeUpload(event.serialize());
  },
  onComplete: (filePresenters) => {
    FilePresenter.updateEachByIdAndRecache(filePresenters, cachedFilePresenters);
    const event = new EventPresenter({ filePresenters });
    logger.info('onComplete', { ...event });
    onComplete(event.serialize());
  },
  onSuccess: (filePresenters) => {
    FilePresenter.updateEachByIdAndRecache(filePresenters, cachedFilePresenters);
    const event = new EventPresenter({ filePresenters });
    logger.debug('onSuccess', { ...event });
    onSuccess(event.serialize());
  },
  onError: (filePresenters) => {
    FilePresenter.updateEachByIdAndRecache(filePresenters, cachedFilePresenters);
    const event = new EventPresenter({ filePresenters });
    logger.error('onError', { ...event });
    onError(event.serialize());
  },
  onFileRemoved: (filePresenters) => {
    FilePresenter.updateEachByIdAndRecache(filePresenters, cachedFilePresenters);
    const event = new EventPresenter({ filePresenters });
    logger.debug('onFileRemoved', { ...event });
    onFileRemoved(event.serialize());
  },
});

const defaultLogger = {
  info: defaultFn,
  warn: defaultFn,
  error: defaultFn,
  debug: defaultFn,
};

const UppyClient = (props = {}, pluginsList = []) => {
  const logger = props.logger || defaultLogger;

  const cachedFilePresenters = [];
  const {
    onComplete,
    onSuccess,
    onError,
    onBeforeUpload,
    onFileRemoved,
  } = callbackHandlers(props, { cachedFilePresenters, logger });

  const uppy = new Uppy({
    id: props.clientId || 'uppy',
    logger,
    debug: props.debug,
    autoProceed: props.autoProceed,
    restrictions: {
      minFileSize: 1, // min 1 byte, don't allow upload an empty file
      maxFileSize: parseInt(props.maxSize, 10),
      allowedFileTypes: FileTypesConfig(props).extensions,
      maxNumberOfFiles: props.multiple ? props.maxFiles : 1,
    },
    onBeforeFileAdded: (file) => preprocessFile(uppy, file),
    onBeforeUpload: (files) => {
      if (!validateFileNames(uppy, files)) {
        return false;
      }
      const preparedFiles = renameFiles(files);
      const filePresenters = Object.values(preparedFiles).map(
        (uppyFile) => new FilePresenter({ uppyFile, uploadAt: new Date() })
      );
      onBeforeUpload(filePresenters);
      return preparedFiles;
    }
  });

  uppy.setMeta({
    requestedStorePath: props.path,
    requestedStoreBucket: props.bucket,
    requestedStoreAccess: props.storeAccess
  });

  const plugins = [...pluginsList];
  const tusConfig = TusdConfig(props);

  plugins.push([LoadingStatus, {}]);
  plugins.push([RemoteImageDownloader, tusConfig]);
  plugins.push([Tus, tusConfig]);
  plugins.push([EnhancedImageEditor, ImageEditorConfig(props)]);

  if (convertOptionsPresent(props)) {
    uppy.opts.convertOptions = props.convertOptions;
    plugins.push([EnhancedCompressor, CompressorConfig(props)]);
  }

  plugins.forEach((p) => uppy.use(...p));

  uppy.on('upload-error', (file) => {
    const fp = new FilePresenter({ file, isSuccessful: false });
    onError([fp]);
  });
  uppy.on('complete', UploadCompleteHandler(tusConfig, onComplete, onSuccess, onError));
  uppy.on('file-removed', (uppyFile, reason) => onFileRemoved([new FilePresenter({ uppyFile, reason })]));

  const editorHandler = ImageEditorHandler(uppy);
  uppy.on('file-editor:start', editorHandler.onStart);
  uppy.on('file-editor:complete', editorHandler.onFinish);
  uppy.on('file-editor:cancel', editorHandler.onFinish);

  return uppy;
};

export default UppyClient;
